import { get } from 'lodash';
import * as _ from 'lodash'
import { FieldArray, FormikProps } from 'formik';
import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import Flatpickr from 'react-flatpickr'
import {Spanish} from 'flatpickr/dist/l10n/es.js'
import { IMetadataItem, IMetadataSection, IMetadataType, IMetadataTypeCheck, ControlTypes } from '../../modules/report-metadata/ReportMetadata.model';

interface ReportRendererProps {
  collection?: any[]
  idVisible?: boolean
  collapsed?: boolean
  disabled?: boolean
  formik?: FormikProps<any>
  path?: string
  depth?: number
  parameters?: {
    [key: string]: any
  }
}

export const ReportRenderer = ({
  collection,
  path,
  depth = 0,
  formik,
  idVisible = true,
  collapsed = false,
  disabled = false,
  parameters = {},
}: ReportRendererProps) => {
  const intl = useIntl()
  const [isCollapsed, setIsCollapsed] = useState(collapsed)

  const validateConditional = (
    item: IMetadataItem,
    parameters?: {
      [key: string]: any
    }
  ) => {
    return (
      parameters &&
      item.conditionalParameter &&
      parameters.hasOwnProperty(item.conditionalParameter) &&
      parameters[item.conditionalParameter] === item.conditionalParameterValue
    )
  }

  const getSampleOptions = (sampleMaxSize: number) => {
    let content: any[] = []
    for (let i = 0; i <= sampleMaxSize; i++) {
      content.push(
        <option key={i} value={i}>
          {i}
        </option>
      )
    }
    return content
  }

  const getInputMode = (
    item: IMetadataItem
  ): 'text' | 'numeric' | 'decimal' => {
    if (!item || !item.typeCheck) {
      return 'text'
    }
    if (item.typeCheck === IMetadataTypeCheck.INTEGER) {
      return 'numeric'
    }
    if (item.typeCheck === IMetadataTypeCheck.FLOAT) {
      return 'decimal'
    }
    if (item.typeCheck === IMetadataTypeCheck.STRING) {
      return 'text'
    }
    return 'text'
  }

  const renderLabelControl = (item, path: string) => {
    const labelAtPath = formik ? get(formik.values, `${path}.label`) : item.label
    const valueAtPath = formik ? get(formik.values, `${path}.value`) : item.value
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {labelAtPath?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <div className='row'>
            <div className='col-xxl-12'>
              <label className='d-flex flex-column-auto flex-start align-items-start fs-6 col-form-label col-form-label-sm ps-5'>
                <span className='text-gray-700'>{valueAtPath?.toUpperCase() ?? ''}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderNumberInputControl = (
    item,
    path: string,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const valueAtPath = formik ? get(formik.values, `${path}.value`) : item.value
    const metricValueAtPath = formik ? get(formik.values, `${path}.metricValue`) : item.metricValue
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {item.label?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <div className='row'>
            <div
              className={classNames('py-1', {
                'col-xxl-9': item.metricOptions,
                'col-xxl-12': !item.metricOptions,
              })}
            >
              <input
                type='text'
                inputMode={getInputMode(item)}
                className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                placeholder={item.placeholder ?? ''}
                {...formik?.getFieldProps(`${path}.value`)}
                onChange={formik?.handleChange}
                disabled={disabled}
                required={item.required}
                value={valueAtPath ?? ''}
              />
              <div className='d-flex flex-row p-1'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {formik &&
                      get(formik.touched, `${path}.value`) &&
                      get(formik.errors, `${path}.value`) ? (
                        <span className='fs-8 text-danger'>
                          {get(formik.errors, `${path}.value`)?.toUpperCase() ?? ''}
                        </span>
                      ) : (
                        <>
                          {parameters &&
                            validateConditional(item, parameters) &&
                            valueAtPath?.length > 0 && (
                              <span className='fs-8 text-danger'>
                                {item.conditionalNote?.toUpperCase() ?? ''}
                              </span>
                            )}
                        </>
                      )}
                    </div>

                    {formik && item.type === ControlTypes.INPUT_TEXT && valueAtPath?.length > 0 && (
                      <div className='d-flex flex-row-auto flex-end align-items-start'>
                        <span className='fs-8 text-muted'>{`${valueAtPath?.length || 0}/255`}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {item.metricOptions && (
              <div className='col-xxl-3 py-1'>
                <select
                  className='form-select form-select-solid form-select-sm fs-6 text-dark'
                  {...formik?.getFieldProps(`${path}.metricValue`)}
                  onChange={formik?.handleChange}
                  disabled={disabled}
                  value={metricValueAtPath ?? ''}
                >
                  <option value={''}>{intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}</option>
                  {item.metricOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderTextInputControl = (
    item,
    path: string,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const valueAtPath = formik ? get(formik.values, `${path}.value`) : item.value
    const metricValueAtPath = formik ? get(formik.values, `${path}.metricValue`) : item.metricValue
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {item.label?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <div className='row'>
            <div
              className={classNames('py-1', {
                'col-xxl-9': item.metricOptions,
                'col-xxl-12': !item.metricOptions,
              })}
            >
              <input
                type='text'
                inputMode={getInputMode(item)}
                className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                placeholder={item.placeholder ?? ''}
                {...formik?.getFieldProps(`${path}.value`)}
                onChange={formik?.handleChange}
                disabled={disabled}
                required={item.required}
                value={valueAtPath ?? ''}
              />
              <div className='d-flex flex-row p-1'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {formik &&
                      get(formik.touched, `${path}.value`) &&
                      get(formik.errors, `${path}.value`) ? (
                        <span className='fs-8 text-danger'>
                          {get(formik.errors, `${path}.value`)?.toUpperCase() ?? ''}
                        </span>
                      ) : (
                        <>
                          {parameters &&
                            validateConditional(item, parameters) &&
                            valueAtPath?.length > 0 && (
                              <span className='fs-8 text-danger'>
                                {item.conditionalNote?.toUpperCase() ?? ''}
                              </span>
                            )}
                        </>
                      )}
                    </div>

                    {formik && item.type === ControlTypes.INPUT_TEXT && valueAtPath?.length > 0 && (
                      <div className='d-flex flex-row-auto flex-end align-items-start'>
                        <span className='fs-8 text-muted'>{`${valueAtPath?.length || 0}/255`}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {item.metricOptions && (
              <div className='col-xxl-3 py-1'>
                <select
                  className='form-select form-select-solid form-select-sm fs-6 text-dark'
                  {...formik?.getFieldProps(`${path}.metricValue`)}
                  onChange={formik?.handleChange}
                  disabled={disabled}
                  value={metricValueAtPath ?? ''}
                >
                  <option value={''}>{intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}</option>
                  {item.metricOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderTextAreaControl = (
    item,
    path: string,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const valueAtPath = formik ? get(formik?.values, `${path}.value`) : item.value
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {item.label?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <textarea
            rows={item.value ? 5 : 2}
            className='form-control form-control-sm form-control-solid px-5 py-5 fs-6 text-dark'
            placeholder={item.placeholder ?? ''}
            {...formik?.getFieldProps(`${path}.value`)}
            onChange={formik?.handleChange}
            disabled={disabled}
            required={item.required}
            value={valueAtPath ?? ''}
          />
          <div className='d-flex flex-row p-1'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {formik &&
                  get(formik.touched, `${path}.value`) &&
                  get(formik.errors, `${path}.value`) ? (
                    <span className='fs-8 text-danger'>
                      {get(formik.errors, `${path}.value`)?.toUpperCase() ?? ''}
                    </span>
                  ) : (
                    <>
                      {parameters &&
                        validateConditional(item, parameters) &&
                        valueAtPath?.length > 0 && (
                          <span className='fs-8 text-danger'>
                            {item.conditionalNote?.toUpperCase() ?? ''}
                          </span>
                        )}
                    </>
                  )}
                </div>

                {formik && valueAtPath?.length > 0 && (
                  <div className='d-flex flex-row-auto flex-end align-items-start'>
                    <span className='fs-8 text-muted'>{`${valueAtPath?.length || 0}/3000`}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSingleSelectionControl = (
    item,
    path: string,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const valueAtPath = formik ? get(formik?.values, `${path}.selectValue`) : item.selectValue
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {item.label?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <select
            className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
            {...formik?.getFieldProps(`${path}.selectValue`)}
            onChange={formik?.handleChange}
            disabled={disabled}
            required={item.required}
            value={valueAtPath ?? ''}
          >
            {!item.required ||
              (!item.selectValue && (
                <option value={''}>{intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}</option>
              ))}
            {item.selectOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className='d-flex flex-row p-1'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {formik &&
                  get(formik.touched, `${path}.value`) &&
                  get(formik.errors, `${path}.value`) ? (
                    <span className='fs-8 text-danger'>
                      {get(formik.errors, `${path}.value`)?.toUpperCase() ?? ''}
                    </span>
                  ) : (
                    <>
                      {parameters &&
                        validateConditional(item, parameters) &&
                        valueAtPath?.length > 0 && (
                          <span className='fs-8 text-danger'>
                            {item.conditionalNote?.toUpperCase() ?? ''}
                          </span>
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDatePickerControl = (
    item,
    path: string,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const valueAtPath = formik ? get(formik?.values, `${path}.value`) : item.value
    return (
      <div className='row'>
        <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm ps-5'>
          <span className={classNames('text-gray-700', {required: formik && item.required})}>
            {item.label?.toUpperCase() ?? ''}
          </span>
        </label>
        <div className='col-xxl-9'>
          <Flatpickr
            className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
            value={valueAtPath ?? ''}
            placeholder={item.placeholder ?? ''}
            options={{
              altFormat: intl.formatMessage({id: 'I18N.LOCALE'}) === 'es' ? 'd-m-Y' : 'm-d-Y',
              altInput: true,
              locale: intl.formatMessage({id: 'I18N.LOCALE'}) === 'es' ? Spanish : undefined,
              monthSelectorType: 'static',
              clickOpens: !disabled,
            }}
            onChange={([date]) => {
              const tzoffset = date.getTimezoneOffset() * 60000 //offset in milliseconds
              const noTimezoneDate = new Date(date.valueOf() - tzoffset).toISOString().slice(0, -1)
              formik && formik.setFieldValue(`${path}.value`, noTimezoneDate)
            }}
          />
          <div className='d-flex flex-row p-1'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {formik &&
                  get(formik.touched, `${path}.value`) &&
                  get(formik.errors, `${path}.value`) ? (
                    <span className='fs-8 text-danger'>
                      {get(formik.errors, `${path}.value`)?.toUpperCase() ?? ''}
                    </span>
                  ) : (
                    <>
                      {parameters &&
                        validateConditional(item, parameters) &&
                        valueAtPath?.length > 0 && (
                          <span className='fs-8 text-danger'>
                            {item.conditionalNote?.toUpperCase() ?? ''}
                          </span>
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderNestedItems = (
    item: IMetadataSection,
    path: string,
    depth: number,
    parameters?: {
      [key: string]: any
    }
  ) => {
    const updateSampleItemRecursively = (
      item: IMetadataSection | IMetadataItem,
      sample: IMetadataItem,
      items: IMetadataItem[],
      sampleSize: number
    ) => {
      if (items.length > sampleSize) {
        //REMOVE ITEM
        items.splice(sampleSize)
      } else if (items.length < sampleSize) {
        //ADD ITEM
        items.push({
          id: `${item.id}.${items.length + 1}`,
          label: sample.label && `${sample.label} #${items.length + 1}`,
          metricOptions: sample.metricOptions,
          metricValue: sample.metricValue,
          selectOptions: sample.selectOptions,
          selectValue: sample.selectValue,
          placeholder: sample.placeholder,
          required: sample.required,
          skipEmpty: sample.skipEmpty,
          title: sample.title,
          type: sample.type,
          typeCheck: sample.typeCheck,
          value: sample.value,
          metadataType: sample.metadataType,
        })
        updateSampleItemRecursively(item, sample, items, sampleSize)
      }
    }

    const updateSampleSectionRecursively = (
      item: IMetadataSection | IMetadataItem,
      sample: IMetadataSection,
      items: IMetadataSection[],
      sampleSize: number
    ) => {
      if (items.length > sampleSize) {
        //REMOVE ITEM
        items.splice(sampleSize)
      } else if (items.length < sampleSize) {
        //ADD ITEM
        items.push({
          id: `${item.id}.${items.length + 1}`,
          title: sample.title && `${sample.title} #${items.length + 1}`,
          metadataType: sample.metadataType,
          items: sample.items,
        })
        updateSampleSectionRecursively(item, sample, items, sampleSize)
      }
    }

    const render = (item, path: string, sampleSize: number) => {
      const items: any[] = _.cloneDeep(item.items ?? [])

      if (item.sample.metadataType === IMetadataType.ITEM) {
        updateSampleItemRecursively(item, item.sample, items, sampleSize)
      }
      if (
        item.sample.metadataType === IMetadataType.SECTION ||
        item.sample.metadataType === IMetadataType.ITEM_GROUP
      ) {
        updateSampleSectionRecursively(item, item.sample, items, sampleSize)
      }
      formik?.setFieldValue(`${path}.items`, items)
    }

    const renderInitial = (item: IMetadataSection, path: string) => {
      if (
        formik &&
        item.sample &&
        !item.sampleSkipped &&
        item.sampleSize &&
        item.items &&
        item.items.length < item.sampleSize
      ) {
        render(item, path, item.sampleSize)
      }
    }

    return (
      <>
        {item.sample && item.sampleSkippable && (
          <div className='row pt-3'>
            <label className='col-xxl-3 fs-6 text-gray-700 col-form-label'>
              <span>
                {item.sampleSkippableLabel ?? intl.formatMessage({id: 'UTIL.OMIT_SECTION'})}
              </span>
            </label>

            <div className='col-xxl-9'>
              <div className='form-check form-switch form-check-custom form-check-solid col-form-label'>
                <input
                  className='form-check-input h-25px w-35px'
                  type='checkbox'
                  {...formik?.getFieldProps(`${path}.sampleSkipped`)}
                  onChange={(e) => {
                    formik &&
                      render(item, path, e.target.value === 'false' ? 0 : item.sampleSize ?? 0)
                    formik?.handleChange(e)
                  }}
                  checked={item.sampleSkipped ?? false}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        )}

        {item.sample && item.sampleResizable && !item.sampleSkipped && (
          <div className='row pt-2'>
            <label className='col-xxl-3 fs-6 text-gray-700 col-form-label'>
              <span>
                {item.sampleResizableLabel || intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
              </span>
            </label>
            <div className='col-xxl-9'>
              <div className='row'>
                <div className='col-xxl-12'>
                  <select
                    className='form-select form-select-solid form-select-sm fs-6 text-dark'
                    {...formik?.getFieldProps(`${path}.sampleSize`)}
                    onChange={(e) => {
                      formik && render(item, path, parseInt(e.target.value))
                      formik?.handleChange(e)
                    }}
                    disabled={disabled}
                    value={item.sampleSize ?? 0}
                  >
                    {getSampleOptions(item.sampleMaxSize ?? 3)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {renderInitial(item, path)}

        <div className='flex'>
          {formik ? (
            <FieldArray name={`${path}.items`}>
              {() =>
                item.items.map((item, index) =>
                  renderRecursively(index, item, `${path}.items.${index}`, ++depth, parameters)
                )
              }
            </FieldArray>
          ) : (
            item.items.map((item, index) =>
              renderRecursively(index, item, `${path}.items.${index}`, ++depth, parameters)
            )
          )}
        </div>
      </>
    )
  }

  const renderRecursively = (key, item, path, depth, parameters) => {
    const renderType = (item, path) => {
      if (item.type === ControlTypes.LABEL) {
        return renderLabelControl(item, path)
      } else if (item.type === ControlTypes.INPUT_NUMBER) {
        return renderNumberInputControl(item, path, parameters)
      } else if (item.type === ControlTypes.INPUT_TEXT) {
        return renderTextInputControl(item, path, parameters)
      } else if (item.type === ControlTypes.TEXTAREA) {
        return renderTextAreaControl(item, path, parameters)
      } else if (item.type === ControlTypes.SINGLE_CHOICE) {
        return renderSingleSelectionControl(item, path, parameters)
      } else if (item.type === ControlTypes.DATE) {
        return renderDatePickerControl(item, path, parameters)
      } else if (item.items) {
        return renderNestedItems(item, path, depth, parameters)
      }
    }

    return (
      <div
        key={key}
        className={classNames({
          'col-12 mb-15': depth === 0,
          'col-12 my-2': depth !== 0,
        })}
      >
        {depth === 0 ? (
          <div className='row'>
            <div
              id={`report_accordion_panel_${item.id}`}
              className='accordion-header d-flex mb-5'
              data-bs-toggle='collapse'
              data-bs-target={`#report_accordion_panel_collapse_${item.id}`}
              aria-expanded='false'
              aria-controls={`report_accordion_panel_collapse_${item.id}`}
            >
              {item.title && (
                <div className='mb-0'>
                  {idVisible && (
                    <span
                      className={classNames({
                        'fs-5 fw-bolder text-primary': depth === 0,
                        'fs-5 fw-bolder': depth !== 0,
                      })}
                    >
                      {`${item.id}. `}
                    </span>
                  )}
                  <span
                    className={classNames({
                      'fs-5 fw-bolder text-primary': depth === 0,
                      'fs-5 fw-bolder': depth !== 0,
                    })}
                  >
                    {`${item.title?.toUpperCase()}`}
                  </span>
                </div>
              )}
            </div>
            <div
              id={`report_accordion_panel_collapse_${item.id}`}
              className={classNames({
                'ms-5 collapse show': !isCollapsed,
                'ms-5 collapse': isCollapsed,
              })}
              aria-labelledby={`report_accordion_panel_${item.id}`}
            >
              <div>{renderType(item, path)}</div>
              <div className='separator mt-10'></div>
            </div>
          </div>
        ) : (
          <div className='row'>
            {item.title && (
              <div>
                {idVisible && (
                  <span
                    className={classNames({
                      'fs-5 fw-bolder text-primary': depth === 0,
                      'fs-5 fw-bolder': depth !== 0,
                    })}
                  >
                    {`${item.id}. `}
                  </span>
                )}
                <span
                  className={classNames({
                    'fs-5 fw-bolder text-primary': depth === 0,
                    'fs-5 fw-bolder': depth !== 0,
                  })}
                >
                  {`${item.title?.toUpperCase()}`}
                </span>
              </div>
            )}
            <div>{renderType(item, path)}</div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='accordion accordion-icon-toggle' id='reportAccordion'>
      <div className='d-flex flex-row-fluid flex-end mb-5'>
        <div
          className='btn btn-sm btn-light-primary'
          onClick={() => setIsCollapsed((prevState) => !prevState)}
        >
          {isCollapsed
            ? intl.formatMessage({id: 'UTIL.EXPAND_ALL'})
            : intl.formatMessage({id: 'UTIL.COLLAPSE_ALL'})}
        </div>
      </div>

      {!formik && collection && (
        <div className='flex'>
          {collection.map((item, index) =>
            renderRecursively(index, item, `${path}.${index}`, depth, parameters)
          )}
        </div>
      )}
      {!collection && formik && path && get(formik.values, path) && (
        <>
          <div className='flex'>
            <FieldArray name={`${path}`}>
              {() =>
                get(formik.values, `${path}`).map((item, index) =>
                  renderRecursively(index, item, `${path}.${index}`, depth, parameters)
                )
              }
            </FieldArray>
          </div>
        </>
      )}
    </div>
  )
}
